import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";
import { formatApiErrorDetail } from "@/utils/apiError";

export default function ForgotPassword() {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Mail className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-heading text-xl font-bold">{t.admin.forgotTitle}</h1>
            <p className="text-sm text-muted-foreground">{t.admin.forgotSubtitle}</p>
          </div>
        </div>
        {done ? (
          <p data-testid="forgot-password-confirmation" className="mt-8 rounded-lg bg-emerald-500/10 px-4 py-3 text-sm text-emerald-600">
            {t.admin.linkSent}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 grid gap-5" data-testid="forgot-password-form">
            {error && (
              <p data-testid="forgot-password-error" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}
            <div className="grid gap-2">
              <Label htmlFor="forgot-email">{t.admin.email}</Label>
              <Input
                id="forgot-email"
                data-testid="forgot-password-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <Button type="submit" data-testid="forgot-password-submit-button" disabled={loading} className="gap-2">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t.admin.sendLink}
            </Button>
          </form>
        )}
        <Link
          to="/admin/login"
          data-testid="back-to-login-link"
          className="mt-6 block text-center text-sm text-muted-foreground hover:text-foreground"
        >
          {t.admin.backToLogin}
        </Link>
      </div>
    </div>
  );
}
